import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBrand } from '../../contexts/BrandContext';
import { 
  TrendingUp, 
  BarChart3, 
  Shield, 
  Zap,
  Play,
  ArrowRight
} from 'lucide-react';

const HeroSection = ({ user, language, onRegister, onLogin }) => {
  const { tt } = useLanguage();
  const { brandName } = useBrand();
  
  const translations = {
    ru: {
      title: 'Торгуйте с уверенностью',
      subtitle: `на платформе ${brandName}`,
      description: 'Присоединяйтесь к миллионам трейдеров по всему миру. Торгуйте валютами, криптовалютами, акциями и товарами с помощью современных инструментов и аналитики.',
      getStarted: 'Зарегистрироваться',
      loginBtn: 'Войти в аккаунт',
      watchVideo: 'Посмотреть видео',
      platformAccess: 'Перейти к торговле',
      whyChoose: `Почему выбирают ${brandName}?`,
      features: [
        {
          icon: TrendingUp,
          title: 'Прибыль до 95%',
          description: 'Высокие выплаты по успешным сделкам'
        },
        {
          icon: BarChart3,
          title: 'Профессиональные графики',
          description: 'TradingView интеграция с индикаторами'
        },
        {
          icon: Shield,
          title: 'Безопасность средств',
          description: 'Надежная защита ваших инвестиций'
        },
        {
          icon: Zap,
          title: 'Мгновенное исполнение',
          description: 'Быстрое открытие и закрытие позиций'
        }
      ],
      stats: [
        { value: '10M+', label: tt('Активных трейдеров') },
        { value: '200+', label: tt('Торговых активов') },
        { value: '24/7', label: tt('Поддержка клиентов') },
        { value: '0.1s', label: tt('Скорость исполнения') }
      ]
    },
    en: {
      title: 'Trade with confidence',
      subtitle: 'on Invested platform',
      description: 'Join millions of traders worldwide. Trade currencies, cryptocurrencies, stocks and commodities with modern tools and analytics.',
      getStarted: 'Register',
      loginBtn: 'Login to Account',
      watchVideo: 'Watch Video',
      platformAccess: 'Go to Platform',
      whyChoose: 'Why choose Invested?',
      features: [
        {
          icon: TrendingUp,
          title: 'Up to 95% profit',
          description: 'High payouts on successful trades'
        },
        {
          icon: BarChart3,
          title: 'Professional charts',
          description: 'TradingView integration with indicators'
        },
        {
          icon: Shield,
          title: 'Funds security',
          description: 'Reliable protection of your investments'
        },
        {
          icon: Zap,
          title: 'Instant execution',
          description: 'Fast opening and closing of positions'
        }
      ],
      stats: [
        { value: '10M+', label: 'Active traders' },
        { value: '200+', label: 'Trading assets' },
        { value: '24/7', label: 'Customer support' },
        { value: '0.1s', label: 'Execution speed' }
      ]
    },
    kk: {
      title: 'Сенімділікпен сауда жасаңыз',
      subtitle: `${brandName} платформасында`,
      description: 'Бүкіл әлем бойынша миллиондаған трейдерлерге қосылыңыз. Заманауи құралдар мен аналитика арқылы валюталар, криптовалюталар, акциялар және тауарлармен сауда жасаңыз.',
      getStarted: 'Саудаға бастау',
      loginBtn: 'Аккаунтқа кіру',
      watchVideo: 'Бейне көру',
      platformAccess: 'Саудаға өту',
      whyChoose: `Неліктен ${brandName} таңдайды?`,
      features: [
        {
          icon: TrendingUp,
          title: '95%-ға дейін пайда',
          description: 'Сәтті мәмілелер бойынша жоғары төлемдер'
        },
        {
          icon: BarChart3,
          title: 'Кәсіби графиктер',
          description: 'Индикаторлармен TradingView интеграциясы'
        },
        {
          icon: Shield,
          title: 'Қаражат қауіпсіздігі',
          description: 'Инвестицияларыңыздың сенімді қорғанысы'
        },
        {
          icon: Zap,
          title: 'Лезде орындау',
          description: 'Позицияларды жылдам ашу және жабу'
        }
      ],
      stats: [
        { value: '10M+', label: tt('Белсенді трейдерлер') },
        { value: '200+', label: tt('Сауда активтері') },
        { value: '24/7', label: 'Қолдау қызметі' },
        { value: '0.1s', label: tt('Орындау жылдамдығы') }
      ]
    }
  };

  const t = translations[language];

  return (
    <div className="relative bg-gradient-to-br from-slate-900 via-green-900 to-emerald-900 min-h-screen flex items-center pt-16">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-black/30"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/15 to-green-600/15"></div>
      
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-green-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="text-white space-y-8">
            <div className="space-y-6">
              <div className="space-y-2">
                <h1 className="text-5xl md:text-7xl font-bold">
                  {t.title}
                </h1>
                <h2 className="text-3xl md:text-4xl font-light text-emerald-300">
                  {t.subtitle}
                </h2>
              </div>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                {t.description}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              {user ? (
                <a
                  href="/trading"
                  className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg text-lg font-semibold hover:from-emerald-700 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {t.platformAccess}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              ) : (
                <>
                  <button
                    onClick={onLogin}
                    className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg text-lg font-semibold hover:from-emerald-700 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    data-testid="hero-login-btn"
                  >
                    {t.loginBtn}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <button
                    onClick={onRegister}
                    className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg text-lg font-semibold hover:bg-white/20 transition-all duration-200 border border-white/20"
                    data-testid="hero-register-btn"
                  >
                    {t.getStarted}
                  </button>
                </>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
              {t.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-emerald-300">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Features */}
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-semibold text-white mb-6">
                {t.whyChoose}
              </h3>
              
              <div className="space-y-6">
                {t.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-r from-emerald-600 to-green-600 rounded-lg flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-gray-300">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;